/**
 * Care Background Agent · frontend/src/components/ChatPanel.tsx
 * Full conversation + real-time streaming display.
 */

import { useEffect, useRef, useMemo } from "react";
import type { Message, Session, LiveEvent } from "../hooks/useSession";

interface Props {
  messages: Message[];
  liveEvents: LiveEvent[];
  session: Session | null;
}

export default function ChatPanel({ messages, liveEvents, session }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  const { isStreaming, streamingText, activeTools } = useMemo(() => {
    const recent = liveEvents.slice(-50);
    const lastType = recent[recent.length - 1]?.type;
    const streaming =
      lastType === "part" ||
      lastType === "thinking" ||
      lastType === "tool_use";

    const text = recent
      .filter((e) => e.type === "part" && (e as Record<string, unknown>).part_type === "text")
      .map((e) => (e as Record<string, string>).text ?? "")
      .join("");

    const tools = [
      ...new Set(
        recent
          .filter((e) => e.type === "tool_use")
          .map((e) => (e as Record<string, string>).tool ?? "")
          .filter(Boolean)
          .slice(-3)
      ),
    ];

    return { isStreaming: streaming, streamingText: text, activeTools: tools };
  }, [liveEvents]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  const formatTime = (ts: string) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="flex flex-col h-full bg-[#0f1117]">
      <div className="flex items-center justify-between px-3 py-2 bg-[#181c2a] border-b border-[#2a2e45] shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm">💬</span>
          <span className="text-xs text-[#8b8fa8] font-medium">Conversation</span>
        </div>
        <span className="text-xs text-[#8b8fa8]">{messages.length} messages</span>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {messages.length === 0 && !isStreaming && (
          <div className="text-center text-[#8b8fa8] text-xs mt-8">
            <div className="text-2xl mb-2">🤖</div>
            <p>No messages yet.</p>
            <p className="mt-1">Care will respond in Google Chat and here.</p>
          </div>
        )}

        {messages.map((msg) => (
          <MessageBubble key={msg.id} msg={msg} formatTime={formatTime} />
        ))}

        {isStreaming && (
          <div className="space-y-2">
            {activeTools.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {activeTools.map((t) => (
                  <span
                    key={t}
                    className="flex items-center gap-1 px-2 py-0.5 bg-[#5b4dff]/10 border border-[#5b4dff]/30 rounded-full text-xs text-[#a89cff]"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#5b4dff] animate-pulse" />
                    {t}
                  </span>
                ))}
              </div>
            )}

            {streamingText && (
              <div className="bg-[#181c2a] border border-[#2a2e45] rounded-xl rounded-tl-sm px-3 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 rounded bg-gradient-to-br from-[#5b4dff] to-[#8b5cf6] flex items-center justify-center text-white text-[8px] font-bold">
                    C
                  </div>
                  <span className="text-xs text-[#5b4dff] font-medium">Care</span>
                  <span className="text-[10px] text-[#8b8fa8]">typing…</span>
                </div>
                <p className="text-[#c4c8e2] text-sm leading-relaxed">{streamingText}</p>
                <span className="inline-block w-1.5 h-4 bg-[#5b4dff] animate-pulse ml-0.5 align-text-bottom" />
              </div>
            )}

            {!streamingText && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#181c2a] border border-[#2a2e45] rounded-xl rounded-tl-sm">
                <div className="w-4 h-4 rounded bg-gradient-to-br from-[#5b4dff] to-[#8b5cf6] flex items-center justify-center text-white text-[8px] font-bold">
                  C
                </div>
                <span className="text-xs text-[#8b8fa8]">Care is working…</span>
                <span className="flex gap-0.5 ml-1">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="w-1 h-1 bg-[#5b4dff] rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 150}ms` }}
                    />
                  ))}
                </span>
              </div>
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {session && (
        <div className="shrink-0 px-3 py-2 border-t border-[#2a2e45] bg-[#181c2a]">
          <div className="flex items-center justify-between text-xs text-[#8b8fa8]">
            <span className="font-mono truncate max-w-[160px]">{session.id}</span>
            <span className="capitalize">{session.status}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({
  msg,
  formatTime,
}: {
  msg: Message;
  formatTime: (ts: string) => string;
}) {
  const isUser = msg.role === "user";
  const isEscalation = msg.content.includes("🚨") && msg.role === "assistant";

  return (
    <div className={`flex flex-col gap-0.5 ${isUser ? "items-end" : "items-start"}`}>
      <div className="flex items-center gap-1.5 px-1">
        {!isUser && (
          <div className="w-4 h-4 rounded bg-gradient-to-br from-[#5b4dff] to-[#8b5cf6] flex items-center justify-center text-white text-[8px] font-bold">
            C
          </div>
        )}
        <span className="text-[10px] text-[#8b8fa8]">
          {isUser ? (msg.sender_name ?? "Engineer") : "Care"}
        </span>
        <span className="text-[10px] text-[#4a4e65]">{formatTime(msg.ts)}</span>
      </div>

      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? "bg-[#5b4dff] text-white rounded-tr-sm"
            : isEscalation
            ? "bg-orange-500/10 border border-orange-500/30 text-orange-200 rounded-tl-sm"
            : "bg-[#181c2a] border border-[#2a2e45] text-[#c4c8e2] rounded-tl-sm"
        }`}
      >
        <pre className="whitespace-pre-wrap font-sans">{msg.content}</pre>
      </div>
    </div>
  );
}
